import { JACKPOT_RULES_LIST } from '@constants/jackpotConstants'

// Merges the top-contributions leaderboard with the current user's own entry
// (the BE already splits these — `top` never includes "me" separately, unlike
// defenderV2's REST response which sends one flat list and does this same
// merge client-side by comparing userId against the session's playerId).
// If I'm not already in the top list, I get prepended so I can always see
// myself without scrolling.
export const combineContributions = (top = [], currentUser = []) => {
  const me = currentUser[0]
  const topRows = top.map((row) => ({ ...row, isMe: false }))
  if (!me) return topRows

  const alreadyInTop = topRows.some((row) => row.userId === me.userId)
  return alreadyInTop ? topRows : [{ ...me, isMe: true }, ...topRows]
}

// "1st"/"2nd"/"3rd"/"4th"... ports defenderV2's getOrdinal exactly.
export const ordinalPosition = (position) => {
  const n = Number(position)
  if (!Number.isFinite(n)) return String(position ?? '')
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  const suffixes = ['th', 'st', 'nd', 'rd']
  return `${n}${suffixes[n % 10] ?? 'th'}`
}

// Live win-chance/contribution percentage display — null while unknown
// (distinguishes "still loading" from "confirmed zero", per defenderV2's
// IS_LEADERBOARD_LOADING = value === null check).
export const formatPercentage = (value) =>
  value == null ? null : `${Number(value).toFixed(2)}%`

// Ports the {currencyCode} placeholder substitution in the static rules copy.
export const formatJackpotRules = (currencyCode) =>
  JACKPOT_RULES_LIST.map((rule) => rule.replaceAll('{currencyCode}', `${currencyCode ?? ''} `))

// Expands a [start, end] ticket range into individual ticket ids — the BE
// sends ranges (bet-wise contributions can span thousands of tickets), the UI
// renders one chip per id.
export const expandTicketRange = (ticketRange) => {
  if (!Array.isArray(ticketRange) || ticketRange.length !== 2) return []
  const [start, end] = ticketRange
  if (typeof start !== 'number' || typeof end !== 'number' || start > end) return []
  return Array.from({ length: end - start + 1 }, (_, i) => start + i)
}

// Per-bet ticket groups for the "view my tickets for this bet" screen — ports
// defenderV2's ViewUserBetWiseContributedTickets derivation (expand ranges,
// flag the bet the user drilled in from, total count).
export const deriveBetTicketGroups = (contributions, selectedBetId) => {
  const groups = (contributions ?? []).map((item) => ({
    ...item,
    isSelectedBet: item.betId === selectedBetId,
    ticketIds: expandTicketRange(item.ticketRange),
  }))

  const totalTickets = groups.reduce((sum, item) => sum + item.ticketIds.length, 0)
  return { groups, totalTickets }
}
